import { Link } from 'react-router-dom';

import {
  brokenPartLabel,
  brokenReportContextLabel,
  formatBrokenReportDate,
  type BrokenContainerReportData,
} from '@/components/BrokenContainerReports/brokenContainerReportFormat';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { BrokenContainerPart } from '@/graphql/generatedTypes';
import { useBrokenContainerReportsQuery } from '@/graphql/queries/brokenContainerReports.generated';
import i18n from '@/utils/i18n';
import { Routes } from '@/utils/routes';

const DAYS = 90;

export function BrokenContainersTab({ storeTextId }: { storeTextId: string }) {
  const { data, loading, error } = useBrokenContainerReportsQuery({
    variables: { storeId: storeTextId, days: DAYS },
    fetchPolicy: 'cache-and-network',
  });
  const reports = data?.brokenContainerReports ?? [];

  return (
    <div className="tw:p-4">
      <Card>
        <CardHeader>
          <CardTitle>{i18n.t('BrokenContainers:cardTitle')}</CardTitle>
          <CardDescription>
            {i18n.t('BrokenContainers:storeTabDescription', { days: DAYS })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && !data ? (
            <Skeleton className="tw:h-24 tw:w-full" />
          ) : error && !data ? (
            <p className="tw:text-sm tw:text-muted-foreground">
              {i18n.t('BrokenContainers:errorLoading')}
            </p>
          ) : reports.length === 0 ? (
            <p className="tw:text-sm tw:text-muted-foreground">
              {i18n.t('BrokenContainers:noReports', { days: DAYS })}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{i18n.t('BrokenContainers:columnDate')}</TableHead>
                  <TableHead>
                    {i18n.t('BrokenContainers:columnContext')}
                  </TableHead>
                  <TableHead className="tw:text-right">
                    {i18n.t('BrokenContainers:columnTotal')}
                  </TableHead>
                  <TableHead>
                    {i18n.t('BrokenContainers:columnLines')}
                  </TableHead>
                  <TableHead className="tw:text-right">
                    {i18n.t('BrokenContainers:columnPhotos')}
                  </TableHead>
                  <TableHead>
                    {i18n.t('BrokenContainers:columnOrder')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell className="tw:whitespace-nowrap">
                      {formatBrokenReportDate(report.created_at)}
                    </TableCell>
                    <TableCell>
                      {brokenReportContextLabel(report.context)}
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {report.container_total}
                    </TableCell>
                    <TableCell className="tw:whitespace-normal">
                      {linesSummary(report)}
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {photoCount(report)}
                    </TableCell>
                    <TableCell>
                      {report.merchant_order_id &&
                      report.merchant_order_delivery_note_id != null ? (
                        <Link
                          to={Routes.orderDetails(report.merchant_order_id)}
                          className="tw:text-primary tw:underline-offset-4 tw:hover:underline"
                        >
                          {i18n.t('BrokenContainers:returnOrderLink', {
                            deliveryNoteId:
                              report.merchant_order_delivery_note_id,
                          })}
                        </Link>
                      ) : (
                        <span className="tw:text-muted-foreground">–</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function photoCount(report: BrokenContainerReportData) {
  return report.lines.reduce(
    (count, line) => count + line.photos.length,
    report.photos.length,
  );
}

function linesSummary(report: BrokenContainerReportData) {
  const totals = new Map<string, { label: string; amount: number }>();
  for (const line of report.lines) {
    const key = `${line.container_type_id}-${line.part}`;
    const name = line.container_type_name ?? `#${line.container_type_id}`;
    const label =
      line.part === BrokenContainerPart.QrCode
        ? `${name} (${brokenPartLabel(line.part)})`
        : name;
    const entry = totals.get(key) ?? { label, amount: 0 };
    entry.amount += line.amount;
    totals.set(key, entry);
  }
  return [...totals.values()]
    .map(({ label, amount }) => `${amount} × ${label}`)
    .join(', ');
}
